import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { useReferralInvitedQuery, useReferralMeQuery, useReferralUsesQuery } from "../../Redux/Apis/referralApi";
import { fa_dateـetime, fa_number } from "../../utils/Persian";

function Referral() {
  const { data: me } = useReferralMeQuery()
  const { data: invited } = useReferralInvitedQuery()
  const { data: uses } = useReferralUsesQuery()

  // Build a fallback share link if backend didn't return one
  const inviteLink = useMemo(() => {
    if (me?.link) return me.link
    if (me?.code) return `${window.location.origin}/?ref=${encodeURIComponent(me.code)}`
    return ''
  }, [me])

  // Compute stats fallback if backend fields are absent
  const derived = useMemo(() => {
    const invitedCount = invited?.length ?? 0
    const successCount = (invited ?? []).filter(it => it.purchased || it.status === 'PURCHASED').length
    const pendingCount = invitedCount - successCount
    const rewardCredits = successCount * 8
    return { invitedCount, successCount, pendingCount, rewardCredits }
  }, [invited])

  // Only show pending registrations (registered and not purchased) in first table
  const pendingInvited = useMemo(() => {
    return (invited ?? []).filter(it => !(it.purchased || it.status === 'PURCHASED'))
  }, [invited])

  const showNum = (v?: number, fb?: number) => {
    const val = (v ?? fb)
    return val === undefined ? '-' : fa_number(val)
  }

  useEffect(() => {
    // no-op; placeholder if we need side effects later
  }, [])

  const copy = async (text?: string) => {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      toast.success('کپی شد')
    } catch {
      toast.error('کپی انجام نشد')
    }
  }

  const shareLink = async (platform: 'whatsapp' | 'telegram' | 'x' | 'instagram') => {
    // Prefer server-provided share links; otherwise construct simple ones
    const fromServer = (me as any)?.share?.[platform]
    const text = `به GREhub بپیوندید و ۸ اعتبار رایگان بگیرید! \n لینک دعوت من:`
    const url = inviteLink
    const constructed = platform === 'whatsapp'
      ? `https://wa.me/?text=${encodeURIComponent(text + '\n' + url)}`
      : platform === 'telegram'
      ? `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`
      : platform === 'x'
      ? `https://x.com/intent/post?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`
      : ''
    const link = fromServer || constructed
    if (!url) return
    if (platform === 'instagram') {
      try { await navigator.clipboard.writeText(url) } catch {}
      window.open('https://www.instagram.com/', '_blank')
      return
    }
    window.open(link, '_blank')
  }

  return (
    <main className="col-lg-9">
      <div className="panel-surface p-3 mb-3 d-flex align-items-center justify-content-between">
        <h6 className="fw-bold m-0">معرفی به دوستان</h6>
        <span className="panel-muted small">کد و لینک دعوت + گزارش‌ها</span>
      </div>

      {/* Stats on top */}
      <div className="panel-surface p-3 mb-3" dir="rtl">
        <div className="row g-3">
          <div className="col-6 col-lg-3">
            <div className="p-3 rounded text-center bg-primary-subtle border">
              <div className="mb-1 text-primary"><FontAwesomeIcon icon="user-plus"/></div>
              ثبت‌نام‌شده با لینک
              <div className="fs-4 mt-1 fw-bold text-primary">{showNum(me?.stats?.invitedCount, derived.invitedCount)}</div>
            </div>
          </div>
          <div className="col-6 col-lg-3">
            <div className="p-3 rounded text-center bg-warning-subtle border">
              <div className="mb-1 text-warning"><FontAwesomeIcon icon="hourglass-half"/></div>
              در انتظار سفارش
              <div className="fs-4 mt-1 fw-bold text-warning">{showNum(me?.stats?.pendingCount, derived.pendingCount)}</div>
            </div>
          </div>
          <div className="col-6 col-lg-3">
            <div className="p-3 rounded text-center bg-success-subtle border">
              <div className="mb-1 text-success"><FontAwesomeIcon icon="circle-check"/></div>
              سفارش اول موفق
              <div className="fs-4 mt-1 fw-bold text-success">{showNum(me?.stats?.successCount, derived.successCount)}</div>
            </div>
          </div>
          <div className="col-6 col-lg-3">
            <div className="p-3 rounded text-center bg-danger-subtle border">
              <div className="mb-1 text-danger"><FontAwesomeIcon icon="gift"/></div>
              پاداش (اعتبار آزمون)
              <div className="fs-4 mt-1 fw-bold text-danger">{showNum(me?.stats?.rewardCredits, derived.rewardCredits)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Invite/share card with inline rules */}
      <div className="panel-surface p-3 mb-3" dir="rtl">
        <div className="row g-4 align-items-start">
          {/* Right block: stacked items */}
          <div className="col-12 col-lg-7">
            <div className="mb-3">
              <label className="form-label">کد معرف</label>
              <div className="input-group">
                <input className="form-control" value={me?.code ?? ''} readOnly />
                <button className="btn btn-outline-secondary" onClick={() => copy(me?.code)}>
                  <FontAwesomeIcon icon="copy" className="me-1"/>کپی
                </button>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">اشتراک‌گذاری سریع</label>
              <div className="d-flex flex-wrap gap-2">
                <button className="btn btn-success shadow-sm" onClick={() => shareLink('whatsapp')} disabled={!inviteLink}>
                  <FontAwesomeIcon icon={['fab','whatsapp']} className="me-1"/> واتس‌اپ
                </button>
                <button className="btn btn-info text-white shadow-sm" onClick={() => shareLink('telegram')} disabled={!inviteLink}>
                  <FontAwesomeIcon icon={['fab','telegram']} className="me-1"/> تلگرام
                </button>
                <button className="btn btn-dark shadow-sm" onClick={() => shareLink('x')} disabled={!inviteLink}>
                  <FontAwesomeIcon icon={['fab','x-twitter']} className="me-1"/> X
                </button>
                <button className="btn btn-danger shadow-sm" onClick={() => shareLink('instagram')} disabled={!inviteLink}>
                  <FontAwesomeIcon icon={['fab','instagram']} className="me-1"/> اینستاگرام
                </button>
              </div>
            </div>
            <div>
              <label className="form-label">لینک دعوت</label>
              <div className="input-group">
                <input className="form-control" value={inviteLink} readOnly placeholder="لینک هنوز آماده نیست" />
                <button className="btn btn-outline-primary" onClick={() => copy(inviteLink)} disabled={!inviteLink}>
                  <FontAwesomeIcon icon="link" className="me-1"/>کپی لینک
                </button>
              </div>
            </div>
          </div>
          {/* Left block: rules */}
          <div className="col-12 col-lg-5">
            <div className="alert alert-primary border-start border-4 border-primary small mb-0 mt-2" role="alert">
              <div className="d-flex align-items-center mb-2">
                <FontAwesomeIcon icon="circle-info" className="me-2 text-primary"/>
                <strong className="fs-6 text-primary">قوانین و زمان اضافه شدن پاداش</strong>
              </div>
              <ul className="mb-0">
                <li>پاداش ۸ + ۸ اعتبار آزمون خواهد بود. (۸ اعتبار آزمون شما + ۸ اعتبار آزمون دوست شما)</li>
                <li>پس از اولین سفارش موفق دوست، به هر دو کاربر و به طور همزمان پاداش اضافه می‌گردد.</li>
                <li>کد معرف باید موقع ثبت نام در سایت وارد شود و به عنوان کد تخفیف قابل استفاده نیست.</li>
                <li>محدودیتی در دریافت پاداش برای معرف نیست و به ازای تایید اولین سفارش هر دوست، برای معرف ۸ اعتبار آزمون اضافه خواهد شد.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      

      <div className="panel-surface p-3 mb-3" dir="rtl">
        <h6 className="fw-bold mb-3">ثبت‌نام‌شده‌ها</h6>
        <div className="table-responsive">
          <table className="table table-striped">
            <thead>
              <tr>
                <th>نام</th>
                <th>ایمیل</th>
                <th>تاریخ ثبت‌نام</th>
                <th>وضعیت</th>
                <th>پرداخت</th>
              </tr>
            </thead>
            <tbody>
              {pendingInvited.map((row, idx) => (
                <tr key={idx}>
                  <td>{[row.invited?.name, row.invited?.family].filter(Boolean).join(' ') || '-'}</td>
                  <td>{row.invited?.email ?? '-'}</td>
                  <td>{row.invited?.create_date ? fa_dateـetime(row.invited.create_date) : '-'}</td>
                  <td>
                    {row.status === 'PURCHASED' || row.purchased ? (
                      <span className="badge text-bg-success">خرید اول انجام شده</span>
                    ) : (
                      <span className="badge text-bg-warning">ثبت‌نام کرده</span>
                    )}
                  </td>
                  <td>
                    {row.purchase_info?.payment_id ? (
                      <span className="small">#{typeof row.purchase_info.payment_id === 'number' ? fa_number(row.purchase_info.payment_id) : row.purchase_info.payment_id} {row.purchase_info.create_date ? `- ${fa_dateـetime(row.purchase_info.create_date)}` : ''}</span>
                    ) : '-'}
                  </td>
                </tr>
              ))}
              {!pendingInvited.length && (
                <tr><td colSpan={5} className="text-center">موردی ثبت نشده است</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="panel-surface p-3" dir="rtl">
        <h6 className="fw-bold mb-3">دعوت‌های موفق</h6>
        <div className="table-responsive">
          <table className="table table-striped">
            <thead>
              <tr>
                <th>نام معرفی شده</th>
                <th>ایمیل کاربر</th>
                <th>تاریخ پرداخت</th>
                <th>اعتبار دریافتی</th>
              </tr>
            </thead>
            <tbody>
              {uses?.map((row, idx) => (
                <tr key={idx}>
                  <td>{[row.Invited?.name, row.Invited?.family].filter(Boolean).join(' ') || '-'}</td>
                  <td>{row.Invited?.email ?? '-'}</td>
                  <td>{row.create_date ? fa_dateـetime(row.create_date) : '-'}</td>
                  <td>{fa_number(8)} اعتبار</td>
                </tr>
              ))}
              {!uses?.length && (
                <tr><td colSpan={4} className="text-center">موردی ثبت نشده است</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  )
}

export default Referral
